import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import Lice from '@/assets/icons/louse.svg?component';
import { Spinner } from '@/components/atoms/Spinner';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import { EmptyState } from '@/components/molecules/EmptyState';
import FishHealthFilter from '@/components/molecules/FishHealthFilter';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import type { Image as ViewImageType } from '@/components/organisms/ImageViewer';
import { ImageViewer } from '@/components/organisms/ImageViewer';
import type { ImageFeedback } from '@/domain/imageFeedback';
import { LICE_FILTER_TAGS } from '@/domain/images';
import type { DomainDailyLiceCount, DomainLast3DaysLiceCount } from '@/domain/lice';
import type { ImageState } from '@/hooks/image-state';
import { localizedDateFormat } from '@/utils/date';

import styles from './CageLiceDetections.module.scss';

const SHOW_FEMALE_LICE_IMAGE_LIMIT = 0.02;
const SHOW_MOVABLE_LICE_IMAGE_LIMIT = 0.5;
const SHOW_CALIGUS_LICE_IMAGE_LIMIT = 0.5;

interface CageLiceDetectionsPageContentProps {
    last200?: DomainLast3DaysLiceCount & {
        count?: number;
        countNoLice?: number;
        dayPart?: string;
        daypartId?: number;
        total?: number;
    };
    liceCount: DomainDailyLiceCount[];
    isOptoScaleAdmin?: boolean;
    imageState: ImageState;
    isChileanLice?: boolean;
    onCommitImageFeedback?: (imageFeedbackDto: ImageFeedback) => void;
}

export default function CageLiceDetectionsPageContent(props: CageLiceDetectionsPageContentProps) {
    const {
        liceCount,
        isOptoScaleAdmin = false,
        imageState,
        isChileanLice = false,
        onCommitImageFeedback = () => {},
    } = props;
    const { t } = useTranslation();

    const lastRecord = liceCount[liceCount.length - 1];
    const showLiceImages =
        (lastRecord?.femaleAvg ?? 0) >= SHOW_FEMALE_LICE_IMAGE_LIMIT ||
        (lastRecord?.movingStageAvg ?? 0) >= SHOW_MOVABLE_LICE_IMAGE_LIMIT ||
        (lastRecord?.caligusElongatusAvg ?? 0) >= SHOW_CALIGUS_LICE_IMAGE_LIMIT;

    const last = imageState.dayswithimages[imageState.dayswithimages.length - 1];

    const [selectedImageFilterTag, setSelectedImageFilterTag] = useState<string>(
        LICE_FILTER_TAGS[0]?.tag ?? 'all'
    );

    const onFilterGroupChangeHandler = (tag: string) => {
        setSelectedImageFilterTag(tag);
    };

    const liceStageFilter = useCallback(
        (image: ViewImageType): boolean => {
            const currentFilter = LICE_FILTER_TAGS.find((f) => f.tag === selectedImageFilterTag);
            return currentFilter ? (currentFilter.filter(image) ?? false) : true;
        },
        [selectedImageFilterTag]
    );

    // Memoised so the array reference only changes when the images or the selected
    // filter actually change. An unstable reference here made the image viewer reset
    // to the first image of the day on every re-render (WB-2143).
    const filteredImages = useMemo(
        () => imageState.images.filter(liceStageFilter),
        [imageState.images, liceStageFilter]
    );

    return (
        <div className={styles.pageBoxLayout}>
            <div>
                <ContentBlock size="compact" spacing="none">
                    {showLiceImages || isOptoScaleAdmin ? (
                        imageState.loading ? (
                            <Spinner size="lg" />
                        ) : (
                            <>
                                <ImageViewer
                                    variant="liceDetections"
                                    images={filteredImages}
                                    isOptoScaleAdmin={isOptoScaleAdmin}
                                    onCommitImageFeedback={onCommitImageFeedback}
                                    options={{
                                        boxes: 'on',
                                        showHelp: false,
                                        liceRegion: isChileanLice ? 'chile' : 'norway',
                                    }}
                                    daysWithImages={imageState.dayswithimages}
                                    onDayChange={imageState.onAfterTimelineChangeHandler}
                                    initialImageIndex={imageState.initialImageIndex}
                                    isFetching={imageState.isFetching}
                                />
                                <FishHealthFilter
                                    filterStateList={LICE_FILTER_TAGS}
                                    onFilterGroupChange={onFilterGroupChangeHandler}
                                />
                            </>
                        )
                    ) : (
                        <EmptyState
                            size="spacious"
                            title={t('cageLice.detections.noLiceImagesHeading')}
                            description={t('cageLice.detections.noLiceImagesMessage')}
                        />
                    )}
                </ContentBlock>
            </div>

            <ContentBlock
                title={t('common.labels.information')}
                subtitle={t('cageLice.detections.showingImagesWhereLiceHas')}
                titleIcon={<TitleIcon icon={Lice} />}>
                <div className={styles.rightSideElementContainer}>
                    {last && (
                        <MetricSummaryRow
                            flush
                            label={t('common.labels.lastMeasurement')}
                            value={localizedDateFormat({
                                dateString: last.day,
                                onlydate: true,
                                locale: i18n.language,
                            })}
                        />
                    )}
                </div>
            </ContentBlock>
        </div>
    );
}
